<template>
  <b-card v-if="item">
    <div class="col-md-12 main-invoice-section" v-if="!auditTrial">
      <div class="row justify-content-between mr-0 mb-2">
        <div class="col-md-6">
          <h3 class="font-weight-bolder">
            <b-link class="btn btn-outline-primary" to="/invoices">
              <FeatherIcon icon="ChevronLeftIcon"></FeatherIcon>
              {{ $t("back") }}
            </b-link>
            {{ $t("invoice_id") }} : I-{{ item.id }}
          </h3>
        </div>
        <div align="end">
          <InvoiceStatus :invoice="item" :status="item.status"/>
          <div>
            <h4>
              <b-badge :variant="getInvoiceBadgeVariant(item)">
                {{ $t(getInvoiceBadgeText(item)) }}
              </b-badge>
            </h4>
          </div>
        </div>
      </div>
      <b-row class="ml-1 mr-1 mb-2" v-if="item.status == 2 && item.msg != null">
        <h4>{{ $t('rejected_reason') }}</h4>
      </b-row>
      <b-row class="ml-1 mr-1" v-if="(item.status == 12 || item.status == 2) && item.msg != null">
        <b-col :class="(item.status == 12 && item.msg != null) || item.status == 2 ? 'alert alert-danger' : 'alert alert-secondary'
          " class="p-2 col-md-12">
          <h4>
            <b-avatar
                :variant="(item.status == 12 && item.msg != null) || item.status == 2 ? 'light-danger' : 'light-secondary'"
                class="ml-1 mr-1" size="45">
              <feather-icon icon="AlertTriangleIcon" size="21"/>
            </b-avatar>
            <span
                :class="(item.status == 12 || item.status == 2) && item.msg != null ? 'text-danger' : 'text-secondary'"
                class="font-lg" v-if="item.msg">{{
                $t(item.msg)
              }}
            </span>
          </h4>
        </b-col>
      </b-row>

      <div class="pa-0">
        <b-col class="justify-center text-center text-black-50">
          <b-row class="m-0 pb-0">
            <b-col class="pt-1 pb-0" cols="12">
              <div class="invoice-actions">
                <b-button :title="$t('custom_scoring')" @click="openCustomScoring()" class="mr-1 mb-1" v-b-tooltip.hover variant="outline-success">
                  <FeatherIcon icon="ActivityIcon" size="17"></FeatherIcon>
                </b-button>
                <b-button
                    v-for="button in visibleButtons"
                    :key="button.action"
                    :title="button.title"
                    :variant="button.variant"
                    @click="handleButtonClick(button)"
                    class="mr-1 mb-1"
                    v-b-modal="button.modal"
                >
                  <FeatherIcon
                      v-for="icon in button.icons"
                      :key="icon"
                      :icon="icon"
                      size="17"
                  />
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-col>
      </div>

      <div class="mt-2" v-if="item.scoring">
        <b-col class="">
          <div align="end" class="font-weight-bolder text--gray">
            {{ $t("Admin Additional Information") }}
          </div>
          <b-row>
            <b-table
                :fields="fields"
                :items="[item.scoring]"
                hover
                responsive=""
                striped
            >
              <template v-slot:head()="data">
                <table-header :data="data">
                  <div v-if="data.column != 'debtor'"> {{ data.label }}</div>
                  <div v-else>
                    {{ factoringType === 'reverse' ? $t("supplier") : $t("debtor") }}
                  </div>
                </table-header>
              </template>
              <template #cell(score)="">
                <span>{{
                    invoice.weight_score ? invoice.weight_score : ""
                  }}</span>
              </template>
              <template #cell(terms)="">
                <span>{{ invoice.terms }}</span>
              </template>
              <template #cell(created_at)="data">
                <span>{{ data.item.created_at | formatDate }}</span>
              </template>
              <template #cell(seller)="">
                <span v-if="invoice.company">
                  <span> {{ invoice.company.name }} </span>
                  <span v-html="' ('"> </span>
                  <span v-if="invoice.seller_score">
                    {{ invoice.seller_score }}
                  </span>
                  <span v-html="')'"> </span>
                  <span v-if="invoice.user"> ({{ invoice.user.name }} {{ invoice.user.last_name }})</span>
                </span>
              </template>
              <template #cell(debtor)="">
                <span v-if="invoice.debtor">
                  <span> {{ invoice.debtor.name }} </span
                  ><span v-html="'('"> </span>
                  <span v-if="invoice.debtor_score">
                    {{ invoice.debtor_score }}
                  </span>
                  <span v-html="')'"> </span>
                </span>
              </template>
            </b-table>
          </b-row>
        </b-col>
      </div>
      <div class="mt-2" v-if="item.scoring">
        <b-col class="alert alert-success p-2 p-md-3 text-black-50">

          <b-row class="m-0 mb-3" v-if="item.status == 1">
            <b-col class="d-flex align-items-center flex-wrap">
              <b-avatar size="45" variant="light-success" class="mr-2 mb-2 mb-sm-0">
                <feather-icon icon="AlertTriangleIcon" size="25"/>
              </b-avatar>
              <span class="mb-25 font-weight-bolder">
                {{ $t("congrats") }}
                {{ $t("your_invoice_is_eligible_for_sale") }}
              </span>
            </b-col>
          </b-row>

          <b-row class="m-0 info-row">
            <b-col cols="12" sm="6" md="2" class="info-card">
              <div class="info-content">
                <span class="info-label">{{ $t("rating") }}</span>
                <span class="info-value">{{ invoice.scoring.rating }}</span>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="3" class="info-card">
              <div class="info-content">
                <span class="info-label">{{ $t("effective_value") }}</span>
                <span class="info-value">{{ amountFormat(CalculationsHelper.getLiquidityAmount(item), item.currency.name) }}</span>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="3" class="info-card" v-if="item.is_reverse != 1">
              <div class="info-content">
                <span class="info-label">{{ $t("proof_of_ownership") }}</span>
                <span class="info-value">
                  <span v-if="item.debtor_confirmed == 1">
                    {{ $t("confirmed") }}
                  </span>
                  <span v-else-if="factoringType === 'recourse'">
                    {{ $t("not_needed") }}
                  </span>
                  <span v-else>
                    {{ $t("required") }}
                  </span>
                </span>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="3" class="info-card" v-if="factoringType === 'reverse'">
              <div class="info-content">
                <span class="info-label">{{ $t("repayment_amount") }}</span>
                <span class="info-value">{{ item ? amountFormat(CalculationsHelper.getInvestorRepaymentAmount(item), item.currency.name) : 0 }}</span>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="2" class="info-card">
              <div class="info-content">
                <span class="info-label">{{ $t("type") }}</span>
                <span class="info-value">
                  {{ factoringType === 'reverse' ? $t("Reverse") : (factoringType === 'non_recourse' ? $t("Non-Recourse") : $t("Recourse")) }}
                </span>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="2" class="info-card">
              <div class="info-content">
                <span class="info-label">{{ $t("auto_remove_days") }}</span>
                <div class="info-value d-flex align-items-center">
                  <span>{{ item ? item.auto_remove_days : '' }}</span>
                  <set-auto-remove-days-admin
                      :item-id="item.id"
                      :current-value="item.auto_remove_days"
                      @updated="item.auto_remove_days = $event"
                  />
                </div>
              </div>
            </b-col>
          </b-row>

          <b-row class="m-0 info-row">
            <b-col cols="12" sm="6" md="2" class="info-card">
              <div class="info-content">
                <span class="info-label">
                  {{ factoringType === 'reverse' ? $t("surcharge_in_%") : $t("discount_in_%") }}
                </span>
                <div class="info-value d-flex align-items-center">
                  <span>{{ CalculationsHelper.getDiscountPercentage(item).toFixed(2) }} %</span>
                  <button @click="modalStatus = true" class="btn btn-sm p-0 ml-2">
                    <feather-icon icon="EditIcon" size="18"/>
                  </button>
                </div>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="3" class="info-card">
              <div class="info-content">
                <span class="info-label">{{ $t("fee_hive_new") }} %</span>
                <div class="info-value d-flex align-items-center">
                  <span>
                    {{ CalculationsHelper.getAequitexFeeSellerPercentage(item).toFixed(2) }} %
                  </span>
                  <button @click="() => { type = 'aequitex-fee-seller'; refundModal = true; }" class="btn btn-sm p-0 ml-2">
                    <feather-icon icon="EditIcon" size="18"/>
                  </button>
                </div>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="3" class="info-card">
              <div class="info-content">
                <span class="info-label">{{ $t("investor-fee") }} %</span>
                <div class="info-value d-flex align-items-center">
                  <span>{{ CalculationsHelper.getAequitexFeeInvestorPercentage(item).toFixed(2) }} %</span>
                  <button @click="() => { type = 'aequitex-fee-investor'; refundModal = true; }" class="btn btn-sm p-0 ml-2">
                    <feather-icon icon="EditIcon" size="18"/>
                  </button>
                </div>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="2" class="info-card">
              <div class="info-content">
                <span class="info-label">{{ $t("refund_percent") }}</span>
                <div class="info-value d-flex align-items-center">
                  <span>{{ CalculationsHelper.getRefundPercentage(item) }} %</span>
                  <button @click="() => { type = 'refund'; refundModal = true; }" class="btn btn-sm p-0 ml-2">
                    <feather-icon icon="EditIcon" size="18"/>
                  </button>
                </div>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="2" class="info-card">
              <div class="info-content">
                <span class="info-label">{{ $t("refund_if_defaults_short") }}</span>
                <div class="info-value d-flex align-items-center">
                  <span>{{ refundIfDefaultsPercentage(invoice) }} %</span>
                  <button @click="() => { type = 'refund_default'; refundModal = true; }" class="btn btn-sm p-0 ml-2">
                    <feather-icon icon="EditIcon" size="18"/>
                  </button>
                </div>
              </div>
            </b-col>
          </b-row>

          <b-row class="m-0 info-row">
            <b-col cols="12" sm="6" md="2" class="info-card">
              <div class="info-content">
                <span class="info-label">
                  {{ factoringType === 'reverse' ? $t("surcharge") : $t("discount") }}
                </span>
                <span class="info-value">{{ amountFormat(CalculationsHelper.getDiscountAmount(item), item.currency.name) }}</span>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="3" class="info-card">
              <div class="info-content">
                <span class="info-label">{{ $t("fee_hive_new") }}</span>
                <span class="info-value">{{ amountFormat(CalculationsHelper.getAequitexFeeSellerAmount(item), item.currency.name) }}</span>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="3" class="info-card">
              <div class="info-content">
                <span class="info-label">{{ $t("investor-fee") }}</span>
                <span class="info-value">{{ amountFormat(CalculationsHelper.getAequitexFeeInvestorAmount(item), item.currency.name) }}</span>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="2" class="info-card">
              <div class="info-content">
                <span class="info-label">{{ $t("refund_amount") }}</span>
                <span class="info-value">{{ amountFormat(CalculationsHelper.getRefundAmount(item), item.currency.name) }}</span>
              </div>
            </b-col>

            <b-col cols="12" sm="6" md="2" class="info-card">
              <div class="info-content">
                <span class="info-label">{{ $t("refund_if_defaults_amount_short") }}</span>
                <span class="info-value">{{ amountFormat(calculateRefundIfDefaults(invoice), item.currency.name) }}</span>
              </div>
            </b-col>
          </b-row>
        </b-col>
      </div>

      <b-form>
        <div class="mt-4">
          <b-row class="mr-1" m-0 p-0 v-if="!load">
            <b-col lg="6" md="6" sm="12" xl="6">
              <b-form-group
                  :label="(factoringType === 'reverse' ? $t('supplier') : $t('deptor')) + ':'"
                  class="font-weight-bolder"
                  id="deptor"
                  label-for="deptor"
                  v-if="showAutoSuggest"
              >
                <validation-provider
                    #default="{ errors }"
                    name="debtor"
                    rules="required"
                >
                  <b-form-select
                      :options="companies"
                      disabled
                      text-field="name"
                      v-model="item.issuer_id"
                      value-field="id"
                  >
                  </b-form-select>

                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                  :label="(factoringType === 'reverse' ? $t('supplier') : $t('deptor')) + ':'"
                  class="font-weight-bolder"
                  id="deptor"
                  label-for="deptor"
                  v-else
              >
                <vue-autosuggest
                    :input-props="{
                    id: 'autosuggest__input',
                    class: 'form-control',
                  }"
                    :limit="10"
                    :suggestions="filteredOptions"
                    @keyup="searchForCompaniesName(item)"
                    @selected="onSelected($event, item)"
                    v-model="item.company_name"
                >
                  <template slot-scope="{ suggestion }">
                    <span class="my-suggestion-item"
                    >{{ suggestion.item }}
                    </span>
                  </template>
                </vue-autosuggest>
                <b-button
                    class="mt-1"
                    disabled
                    v-if="showHint"
                    variant="primary"
                >
                  <b-spinner small type="grow"></b-spinner>
                  {{ $t("loading") }}...
                </b-button>
                <div
                    class="alert alert-danger p-1 mt-1"
                    v-if="companyMessage.length > 0"
                >
                  {{ companyMessage }}
                </div>
              </b-form-group>
            </b-col>
            <b-col lg="6" md="6" sm="12" v-if="item.status == 0 && item.debtor == null" xl="6">
              <b-button @click="selectNewDebtor = true" class="mt-md-2">{{ $t("select_debtor") }}
              </b-button>
            </b-col>
            <b-col lg="6" md="6" sm="12" xl="6">
              <b-form-group
                  :label="$t('invoice_amount') + ':'"
                  class="font-weight-bolder"
                  id="amount"
                  label-for="amount"
              >
                <validation-provider
                    #default="{ errors }"
                    name="amount"
                    rules=""
                >
                  <input
                      @focusout="parsedAmount1($event, item.amount)"
                      class="form-control"
                      id="amount-input"
                      placeholder="00'000.00"
                      v-model="item.amount"
                  />

                  <small class="text-danger" v-if="numbersAmountError">{{
                      numbersAmountError
                    }}
                  </small>
                  <br v-if="numbersAmountError"/>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col lg="6" md="6" sm="12" xl="6">
              <b-form-group
                  :label="$t('issue_date') + ':'"
                  class="font-weight-bolder"
                  id="issue_date"
                  label-for="issue_date"
              >
                <validation-provider
                    #default="{ errors }"
                    name="issue_date"
                    rules="required"
                >
                  <b-form-datepicker
                      :locale="
                      $store.state.app && $store.state.app.language == 'de'
                        ? 'de'
                        : 'en'
                    "
                      :max="disabledDates"
                      :placeholder="$t('choose_a_date')"
                      :state="errors.length > 0 ? false : null"
                      @input="changeDueDate(item)"
                      id="issue_date"
                      no-flip
                      required
                      v-model="item.issue_date"
                  ></b-form-datepicker>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col lg="6" md="6" sm="12" xl="6">
              <b-form-group
                  :label="$t('payment_term_in_days')  + ' (' + $t('calculated') + ') :'"
                  class="font-weight-bolder"
                  id="payment_date"
                  label-for="payment_date"
              >
                <validation-provider
                    #default="{ errors }"
                    name="payment_date"
                    rules="required"
                >
                  <b-form-input
                      @input="changeTerms(item)"
                      disabled
                      id="payment_date"
                      required
                      type="number"
                      v-model="item.terms"
                  ></b-form-input>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small class="text-danger" v-if="errorDueDate.length > 0">{{
                      errorDueDate
                    }}
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col lg="6" md="6" sm="12" xl="6" class="nowrap-label">
              <b-form-group
                  :label="$t('due_date') + ':'"
                  class="font-weight-bolder"
                  id="due_date"
                  label-for="due_date"
              >
                <validation-provider
                    #default="{ errors }"
                    name="due_date"
                    rules="required"
                >
                  <b-form-datepicker
                      :locale="
                      $store.state.app && $store.state.app.language == 'de'
                        ? 'de'
                        : 'en'
                    "
                      :placeholder="$t('choose_a_date')"
                      @input="changeTerms(item)"
                      id="due_date"
                      no-flip
                      required
                      v-model="item.due_date"
                  ></b-form-datepicker>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col lg="6" md="6" sm="12" xl="6">
              <b-form-group
                  :label="$t('invoice_number') + ':'"
                  class="font-weight-bolder"
                  id="invoice_num"
                  label-for="due_date"
              >
                <validation-provider
                    #default="{ errors }"
                    name="invoice_num"
                    rules="required"
                >
                  <b-form-input
                      id="invoice_num"
                      required
                      v-model="item.invoice_number"
                  ></b-form-input>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col lg="6" md="6" sm="12" xl="6">
              <b-form-group id="currency" class="font-weight-bolder w-188" :label="$t('currency') + ':'" label-for="currency" clasa="d-flex flex-direction-column">
                <b-dropdown id="currency-dropdown" variant="outline-dark" class="w-50 no-arrow">
                  <template #button-content>
                    <span v-if="currencyName">{{ currencyName }}</span>
                    <span v-else>{{ $t('select_currency') }}</span>
                  </template>

                  <b-dropdown-item v-for="option in currencies" :key="option.id" :value="option.id" @click="changeCurrency(option.id, option.name)">
                    {{ option.name }}
                  </b-dropdown-item>
                </b-dropdown>
              </b-form-group>
            </b-col>

            <b-col
                align="end"
                class="mt-2"
                cols="12"
                lg="6"
                md="6"
                v-if="showUpdateButton"
                xl="6"
            >
              <b-button
                  @click="editInvoice()"
                  variant="primary"
              >
                {{ $t("update_invoice_data") }}
              </b-button
              >
            </b-col>
          </b-row>
          <b-row cols="12" lg="6" md="6" v-else xl="6">
            <img src="/loader.svg"/>
          </b-row>
        </div>
      </b-form>
      <b-row class="mt-2 alert alert-secondary">
        <b-col class="row ml-1 mr-1 pt-2 pb-2">
          <b-col class="pb-0 mb-0 p-2" md="12">
            <b-form-group class="pb-0 mb-0" v-slot="{ ariaDescribedby }">

              <div class="col-12 d-flex justify-content-between flex-wrap" style="gap: 20px">
                <div class="d-flex align-items-center flex-wrap">
                  <b-form-radio
                      :aria-describedby="ariaDescribedby"
                      :title="$t(options[0].title)"
                      class="mr-2"
                      id="nonRecourseTooltip"
                      v-b-tooltip.hover
                      v-model="factoringType"
                      value="non_recourse"
                  >
                    {{ $t('Non-recourse') }}
                  </b-form-radio>

                  <b-form-radio
                      :aria-describedby="ariaDescribedby"
                      :title="$t(options[1].title)"
                      class="mr-2"
                      id="recourseTooltip"
                      v-b-tooltip.hover
                      v-model="factoringType"
                      value="recourse"
                  >
                    {{ $t('Recourse') }}
                    <small>{{ $t('recourse_text') }}</small>
                  </b-form-radio>

                  <b-form-radio
                      :aria-describedby="ariaDescribedby"
                      :title="$t(options[2].title)"
                      class="mr-2"
                      id="reverseTooltip"
                      v-b-tooltip.hover
                      v-model="factoringType"
                      value="reverse"
                  >
                    {{ $t('Reverse') }}
                  </b-form-radio>
                </div>
                <b-button
                    variant="primary"
                    @click="changeInvoiceFact(factoringType)"
                >
                  {{ $t('change') }}
                </b-button>

              </div>
            </b-form-group>
          </b-col>
          <b-col
              class="mt-0"
              lg="12"
              md="12"
              sm="12"
              v-if="factoringType === 'recourse'"
              xl="12"
          >
            <div class="col-12">
              <div v-if="$store.state.app.language == 'en'">
                I hereby confirm the following agreement within the framework of recourse factoring:
                <br/>
                <br/>
                1.1 The Seller transfers its receivables from deliveries and services to the Buyer. The Buyer assumes
                these receivables on account of fulfilment subject to Clause 1.2 and § 3 of this agreement.
                <br/>
                <br/>
                1.2 This is a case of recourse factoring. This means that the seller assumes the default risk for the
                assigned receivables.
                <br/>
                <br/>
                1.3 The debtor pays the amount of the claim to the seller. The seller then forwards the claim amount
                received from the debtor to the buyer within 3 days of receipt.
                <br/>
                <br/>
                1.4 If the debtor has not paid the amount of the claim to the seller by the due date, the seller shall
                pay the amount of the claim to the buyer no later than the third day after the due date (credit note
                required).
                <br/>
                <br/>
                1.5 The Seller shall bear all costs incurred as a result of non-payment, including reminder and
                collection costs.
              </div>
              <div v-else-if="$store.state.app.language == 'de'">
                Hiermit bestätige ich folgende Vereinbarung im Rahmen des Recourse-Factoring:
                <br/>
                <br/>
                1.1 Der Verkäufer überträgt seine Forderungen aus Lieferungen und Leistungen an den Käufer. Der Käufer
                übernimmt diese Forderungen erfüllungshalber vorbehaltlich Ziff. 1.2 und § 3 dieses Vertrages.
                <br/>
                <br/>
                1.2 Es handelt sich um ein Recourse Factoring. Das bedeutet, dass der Verkäufer das Ausfallrisiko für
                die abgetretenen Forderungen übernimmt.
                <br/>
                <br/>
                1.3 Der Schuldner begleicht den Forderungsbetrag an den Verkäufer. Der Verkäufer leitet dann, den durch
                den Schuldner erhaltenen Forderungsbetrag, innerhalb von 3 Tagen nach Erhalt an den Käufer weiter.
                <br/>
                <br/>
                1.4 Falls der Schuldner den Forderungsbetrag bis zum Fälligkeitsdatum nicht an den Verkäufer beglichen
                haben sollte, begleicht der Verkäufer den Forderungsbetrag gegenüber dem Käufer spätestens am dritten
                Tag nach dem Fälligkeitsdatum (Gutschrift erforderlich).
                <br/>
                <br/>
                1.5 Der Verkäufer übernimmt alle Kosten, die durch einen Zahlungsausfall entstehen, einschließlich Mahn-
                und Inkassokosten.
              </div>
            </div>
          </b-col>
          <b-col
              class="mt-0"
              lg="12"
              md="12"
              sm="12"
              v-if="factoringType === 'reverse'"
              xl="12"
          >
            <div class="col-12">
              <div v-if="$store.state.app.language === 'en'">
                I hereby confirm the following agreement within the framework of reverse factoring:
                <br>
                <br>
                1.1 The contractual partner (debtor) transfers its liabilities from deliveries and services to the
                invoice purchaser (factor). This invoice purchaser (factor) shall assume these liabilities on account of
                fulfilment subject to clauses 1.2 and 1.3 of this agreement.
                <br>
                <br>
                1.2 This is a case of reverse factoring. This means that the contractual partner (debtor) retains the
                default risk for the transferred liabilities.
                <br>
                <br>
                1.3 The contractual partner (debtor) pays the amount of its liability to Aequitex AG. The contractual
                partner (debtor) transfers the full amount of his liabilities to Aequitex AG on the due date stated on
                the invoice (credit note required).
                <br>
                <br>
                1.4 The contractual partner (debtor) assumes all costs arising from his payment default, including
                reminder and collection costs.
              </div>
              <div v-else-if="$store.state.app.language === 'de'">
                Hiermit bestätige ich folgende Vereinbarung im Rahmen des Reverse-Factorings:
                <br>
                <br>
                1.1 Der Vertragspartner (Debtor) übergibt seine Verbindlichkeiten aus Lieferungen und Leistungen an den
                Rechnungskäufer (Factor) ab. Dieser Rechnungskäufer (Factor) übernimmt diese Verbindlichkeiten
                erfüllungshalber vorbehaltlich Ziff. 1.2 und 1.3 dieses Vertrages.
                <br>
                <br>
                1.2 Es handelt sich um ein Reverse Factoring. Das bedeutet, dass der Vertragspartner (Debtor) das
                Ausfallrisiko für die übergebenen Verbindlichkeiten behält.
                <br>
                <br>
                1.3 Der Vertragspartner (Debtor) begleicht den Betrag seiner Verbindlichkeit an die Aequitex AG. Der
                Vertragspartner (Debtor) überweist Aequitex AG den vollen Betrag seiner Verbindlichkeiten zum auf der
                Rechnung angegebenen Fälligkeitsdatum (Gutschrift erforderlich).
                <br>
                <br>
                1.4 Der Vertragspartner (Debtor) übernimmt alle Kosten, die durch seinen Zahlungsausfall entstehen,
                einschließlich Mahn- und Inkassokosten.
              </div>
            </div>
          </b-col>
        </b-col>
      </b-row>

      <div v-if="item.fractions_bought && item.fractions_bought.length > 0" >
        <div class="col-md-12 mt-1 pl-0 mr-1 pt-2 pb-2">
          <div align="end" class="font-weight-bolder text--gray">
            {{ $t("fractions_bought") }}
          </div>

          <b-table
              :fields="fractionFields"
              :items="item.fractions_bought"
              id="fraction-table"
              responsive
              striped
          >
            <template #head()="scope">
              <div class="text-nowrap">{{ $t(scope.label) }}</div>
            </template>

            <template #cell(currency)="">
              <span>CHF</span>
            </template>
            <template #cell(buyer)="data">
              <span v-if="data.item.company && data.item.company.name">{{
                  data.item.company.name
                }}</span>
              <span
                  v-else-if="data.item.company.user && data.item.company.user[0]"
              >{{ data.item.company.user[0].name }}
                {{ data.item.company.user[0].last_name }}</span
              >
            </template>

            <template #cell(fraction)="data">
              <span v-if="data.item.fraction"> {{ data.item.fraction }}% </span>
              <span v-else>-</span>
            </template>
            <template #cell(amount)="data">
              <span v-if="data.item.amount">
                {{ data.item.amount | formatAmount }}
              </span>
              <span v-else>-</span>
            </template>
            <template #cell(discount)="data">
              <span v-if="data.item.discount">
                {{ data.item.discount | formatAmount }}
              </span>
              <span v-else>-</span>
            </template>
            <template #cell(hive_fee)="data">
              <span v-if="data.item">
                {{ data.item.hive_fee | formatAmount }}
              </span>
              <span v-else>-</span>
            </template>
            <template #cell(net_return)="data">
              <span v-if="data.item">
                {{ data.item.net_return | formatAmount }}
              </span>
              <span v-else>-</span>
            </template>

            <template #cell(created_at)="data">
              <span v-if="data.item.created_at">{{
                  data.item.created_at | formatDate
                }}</span>
              <span v-else>-</span>
            </template>
            <template #cell(custom)="data">
              <b-button
                  v-if="adminType(['super_admin'])"
                  @click="
                  changeModalInfo(
                    'cancel_fraction',
                    $t('cancel_fraction'),
                    $t('are_you_sure_you_want_to_cancel_this_fraction'),
                    data.item
                  )
                "
                  class="text-end"
                  size="sm"
                  v-b-modal.approveModal
                  variant="danger"
              >{{ $t("cancel") }}
              </b-button>
            </template>
            <template #cell(status)="data">
              <span v-if="data.value == 0">{{ $t("pending") }} </span>
              <span v-if="data.value == 1">{{ $t("approved") }}</span>
              <span v-if="data.value == 2">{{ $t("declined") }}</span>
            </template>
          </b-table>
        </div>
      </div>
      <b-row class="mt-2">
        <b-col class="row blue-jumbotron row mt-1 ml-1 mr-1 pt-2 pb-2">
          <b-row class="justify-content-between col-md-12">
            <b-col class="">
              <h6>{{ $t("associated_files") }}</h6>
            </b-col>
          </b-row>
          <b-col class="col-md-12">
            <div class="mt-1 row col-md-12">
              <h5 class="mr-10">
                <b-link class="web">
                  <feather-icon
                      @click="readInvoice(invoice)"
                      class="pointer"
                      icon="PaperclipIcon"
                      size="22"
                  />
                </b-link>
                <b-link class="mobile">
                  <feather-icon
                      @click="downloadInvoice(invoice)"
                      class="pointer"
                      icon="PaperclipIcon"
                      size="22"
                  />
                </b-link>
                {{ invoice.title }}
              </h5>
              <div class="ml-10" style="margin-left: 40px">
                {{ invoice.created_at | formatDate }}
              </div>
            </div>
          </b-col>

          <b-col
              class="col-md-12"
              v-if="item.associate_files && item.associate_files.length > 0"
          >
            <div
                :key="index"
                class="mt-1 row col-md-12 align-items-center"
                v-for="(item, index) in item.associate_files"
            >
              <h5 class="mr-10">
                <b-link class="web">
                  <feather-icon
                      @click="downloadFile(item)"
                      class="pointer"
                      icon="PaperclipIcon"
                      size="22"
                  />
                </b-link>

                <b-link class="mobile">
                  <feather-icon
                      @click="downloadFile(item)"
                      class="pointer"
                      icon="PaperclipIcon"
                      size="22"
                  />
                </b-link>
                {{ returnTitle(item) }}

              </h5>
              <div class="ml-10" style="margin-left: 40px">
                {{ item.created_at | formatDate }}
              </div>
              <div class="ml-auto">
                <feather-icon
                    @click="confirmDeleteFile(item)"
                    class="pointer text-danger"
                    icon="Trash2Icon"
                    size="18"
                />
              </div>
            </div>
          </b-col>

          <b-row class="col-md-12 mt-5">
            <b-col
                @click="triggerFileInput"
                class="col-md-12 text-center pointer"
            >
              <feather-icon icon="DownloadIcon" size="22"/>
              {{ $t("upload_a_file_or_drag_files_here") }}
            </b-col>
            <div style="display: none">
              <b-form-file
                  @change="uploadFile($event)"
                  class="mt-1 mb-2 col-md-3"
                  label="Upload Associated Invoice"
                  ref="fileInput1"
                  v-model="associated"
              ></b-form-file>
            </div>
          </b-row>
          <b-row class="text-center"></b-row>
        </b-col>
      </b-row>

      <b-modal centered hide-footer v-model="uploadFileModal">
        <div>
          <h4 class="font-weight-bolder">
            {{ $t("assign_new_document_to_invoice") }}
          </h4>
        </div>
        <div>
          <h5 class="mt-1 mb-1">
            <feather-icon class="pointer" icon="PaperclipIcon" size="20"/>
            {{ $t("file") }}
            :
            {{
              uploadedInvoce && uploadedInvoce.file
                  ? uploadedInvoce.file.name
                  : ""
            }}
          </h5>
        </div>
        <div class="mt-1">
          {{ $t("file_description") }}:
          <b-form-input v-model="fileDesc"></b-form-input>
        </div>
        <div class="mt-1">
          {{ $t("type") }}:
          <b-form-select
              :options="fileTypeOptions"
              v-model="fileType"
          ></b-form-select>
        </div>
        <div align="end" class="col-md-12 pr-0 mt-3">
          <b-button @click="storeData" class="mt-1" variant="primary">
            {{ $t("upload") }}
          </b-button
          >
        </div>
      </b-modal>
    </div>

    <AuditTrial :item="item" @showDetails="showDetails" v-else/>
    <ApproveModal :textContent="modalText" :title="modalTitle" :action="approveModalAction" :invoice="item"/>

    <AddNewCompnayModal
        :company="companyZefixData"
        :invoice="item"
        :showDialog="addNewComp"
        @closeAddCompModal="closeAddCompModal"
        @showSettings="showSettings"
    ></AddNewCompnayModal>

    <MainContactModal
        :company="companyZefixDataMainContact"
        :invoice="item"
        :showDialog="MainContact"
        @closeMainContactModal="closeMainContactModal"
        @showSettings="showSettings"
    ></MainContactModal>
    <b-modal
        hide-header-close
        no-close-on-backdrop
        scrollable
        size="lg"
        v-model="scoringModal"
    >
      <template #modal-footer="{}">
        <b-button @click="cancel()" size="sm" variant="outline-primary">
          {{ $t("back") }}
        </b-button>
        <b-button @click="saveCustomScoring()" size="sm" variant="primary">
          {{ $t("save") }}
        </b-button>
      </template>
      <p class="">
        <h5>Correct Invoice Information</h5>
        <div class="box">
          Seller Score: <b>{{ item.seller_score }}</b>
          <br/>
          Debtor Score: <b>{{ item.debtor_score }}</b>
          <br/>
          Terms: <b>{{ item.terms }}</b>
          <br/>
          Due Days: <b>{{ item.due_days }}</b>
          <br/>
          Amount: <b>{{ item.amount }}</b>
          <br/>
          <hr/>
        </div>
        <validation-observer
            name="Scoring"
            ref="scoringValidation"
        >
          <b-form>
            <h5>{{ $t("custom_scoring") }}</h5>
            <b-card>
              <b-row m-0 p-0>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('seller_score') + ':'"
                      id="seller-score"
                      label-for="seller-score"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Seller Score"
                        rules="required"
                    >
                      <b-input-group class="input-group-merge">

                        <b-form-input
                            :state="errors.length > 0 ? false : null"
                            id="seller-score"
                            required
                            v-model="custom_scoring.seller_score"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('debtor_score') + ':'"
                      id="debtor-score"
                      label-for="debtor-score"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Debtor Score"
                        rules="required"
                    >
                      <b-input-group class="input-group-merge">

                        <b-form-input
                            :state="errors.length > 0 ? false : null"
                            id="main-contact-first-name"
                            required
                            v-model="custom_scoring.debtor_score"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row m-0 p-0>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('terms') + ':'"
                      id="terms"
                      label-for="terms"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Terms"
                        rules="required"
                    >
                      <b-input-group class="input-group-merge">

                        <b-form-input
                            :state="errors.length > 0 ? false : null"
                            id="terms"
                            required
                            v-model="custom_scoring.terms"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('due_days') + ':'"
                      id="due-days"
                      label-for="due-days"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Due Days"
                        rules="required"
                    >
                      <b-input-group class="input-group-merge">

                        <b-form-input
                            :state="errors.length > 0 ? false : null"
                            id="due-days"
                            required
                            v-model="custom_scoring.due_days"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row m-0 p-0>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('amount') + ':'"
                      id="amount"
                      label-for="amount"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Amount"
                        rules="required"
                    >
                      <b-input-group class="input-group-merge">

                        <b-form-input
                            :state="errors.length > 0 ? false : null"
                            disabled
                            id="seller-score"
                            required
                            v-model="custom_scoring.amount"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
          </b-form>
        </validation-observer>
      </p>
    </b-modal>
    <b-modal
        hide-header-close
        no-close-on-backdrop
        scrollable
        size="lg"
        v-model="defaultModal"
    >
      <template #modal-footer="{}">
        <b-button @click="cancelDefault()" size="sm" variant="outline-primary">
          {{ $t("cancel") }}
        </b-button>
        <b-button @click.once="saveInvoiceDefault()" size="sm" variant="outline-danger">
          {{ $t("set_to_default") }}
        </b-button>
      </template>
      <p class="">
        <validation-observer
            name="Defaulting"
            ref="defaultValidation"
        >
          <b-form>
            <h5>{{ $t("set_to_default") }}</h5>
            <b-card>
              <b-row m-0 p-0>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('refund_if_defaults_short') + ':'"
                      id="refund-percent"
                      label-for="refund-percent"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Refund Percent"
                        rules=""
                    >
                      <b-input-group class="input-group-merge">
                        <b-form-input
                            :state="errors.length > 0 ? false : null"
                            disabled
                            id="refund-percent"
                            v-model="defaulting.refund_if_defaults_percent"
                        ></b-form-input>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('refund_if_defaults_amount_short') + ':'"
                      id="refund-amount"
                      label-for="refund-amount"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Refund Amount"
                        rules=""
                    >
                      <b-input-group class="input-group-merge">
                        <b-form-input
                            :state="errors.length > 0 ? false : null"
                            disabled
                            id="refund-amount"
                            v-model="defaulting.refund_if_defaults_amount"
                        ></b-form-input>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row m-0 p-0>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('voluntary_paid_out_percent') + ':'"
                      id="voluntary-paid-out-percent"
                      label-for="voluntary-paid-out-percent"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                          id="voluntary-paid-out-percent"
                          v-model="defaulting.voluntary_paid_out_percent"
                          type="text"
                          @input="sanitizePercent"
                      />
                      <b-input-group-append is-text>%</b-input-group-append>
                    </b-input-group>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('voluntary_paid_out_amount') + ':'"
                      id="voluntary-paid-out-amount"
                      label-for="voluntary-paid-out-amount"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                          disabled
                          id="voluntary-paid-out-amount"
                          :value="calculatedVoluntaryAmount"
                      ></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
          </b-form>
        </validation-observer>
      </p>
    </b-modal>
    <b-modal
        hide-header-close
        no-close-on-backdrop
        scrollable
        size="lg"
        v-model="selectNewDebtor"
    >
      <template #modal-footer="{}">
        <b-button @click="cancelSelectedDebtor()" size="sm" variant="outline-primary">
          {{ $t("back") }}
        </b-button>
        <b-button @click="saveNewDebtor()" size="sm" variant="primary">
          {{ $t("save") }}
        </b-button>
      </template>
      <p class="">
        <validation-observer
            name="newDebtor"
            ref="new_debtor"
        >
          <b-form>
            <h5>{{ $t("select_debtor") }}</h5>
            <b-card>
              <b-col lg="6" md="6" sm="12" xl="6">
                <b-form-group
                    :label="$t('debtor') + ':'"
                    class="font-weight-bolder"
                    id="debtor"
                    label-for="debtor"
                >
                  <validation-provider
                      #default="{ errors }"
                      name="debtor"
                      rules="required"
                  >
                    <b-form-select
                        :options="companies"
                        text-field="name"
                        v-model="selected_company"
                        value-field="id"
                    >
                    </b-form-select>

                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-card>
          </b-form>
        </validation-observer>
        <validation-observer
            name="RegisterCompany"
            ref="companyRegisterValidation"
        >
          <b-form v-if="selected_company">
            <h5>{{ $t("main_contact_person") }}</h5>
            <b-card>
              <b-row m-0 p-0>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('salutation') + ':'"
                      id="main-contact-gender-group"
                      label-for="main-contact-gender"
                  >
                    <b-input-group class="input-group-merge">
                      <b-input-group-prepend is-text>
                        <feather-icon class="cursor-pointer" icon="UserIcon"/>
                      </b-input-group-prepend>
                      <b-form-select
                          :options="[
                          {
                            text: $t('select_one'),
                            value: null,
                            disabled: true,
                          },
                          { text: $t('mr'), value: '0' },
                          { text: $t('mrs'), value: '1' },
                        ]"
                          id="main-contact-gender"
                          required
                          v-model="main_contact_gender"
                      ></b-form-select>
                    </b-input-group>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('company_email_address') + ':'"
                      id="main-contact-email-group"
                      label-for="main-contact-email"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Number of Debtors"
                        rules="required|email"
                    >
                      <b-input-group class="input-group-merge">
                        <b-input-group-prepend is-text>
                          <feather-icon
                              class="cursor-pointer"
                              icon="MailIcon"
                          />
                        </b-input-group-prepend>
                        <b-form-input
                            :state="errors.length > 0 ? false : null"
                            id="main-contact-email"
                            required
                            v-model="main_contact_email"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row m-0 p-0>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('first_name') + ':'"
                      id="main-contact-first-name-group"
                      label-for="main-contact-first-name"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="First name"
                        rules="required"
                    >
                      <b-input-group class="input-group-merge">
                        <b-input-group-prepend is-text>
                          <feather-icon
                              class="cursor-pointer"
                              icon="UserIcon"
                          />
                        </b-input-group-prepend>
                        <b-form-input
                            :disabled="disableInputs"
                            :state="errors.length > 0 ? false : null"
                            id="main-contact-first-name"
                            required
                            v-model="main_contact_first_name"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('tel') + ':'"
                      id="main-contact-tel-group"
                      label-for="main-contact-tel"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="tel"
                        rules="required"
                    >
                      <b-input-group>
                        <b-input-group-prepend id="phoneCountries">
                          <b-form-select
                              :options="mobileCountries"
                              v-model="phoneContries"
                          >
                          </b-form-select>
                        </b-input-group-prepend>
                        <b-form-input
                            :state="errors.length > 0 ? false : null"
                            @input="checkPhoneFormat"
                            class="form-control"
                            id="main-contact-tel"
                            v-model="main_contact_tel"
                        >
                        </b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                      <small class="text-danger" v-if="phoneValidation">{{
                          phoneValidation
                        }}
                      </small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row m-0 p-0>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('last_name') + ':'"
                      id="main-contact-last-name-group"
                      label-for="main-contact-last-name"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Last Name"
                        rules="required"
                    >
                      <b-input-group class="input-group-merge">
                        <b-input-group-prepend is-text>
                          <feather-icon
                              class="cursor-pointer"
                              icon="UserIcon"
                          />
                        </b-input-group-prepend>
                        <b-form-input
                            :disabled="disableInputs"
                            :state="errors.length > 0 ? false : null"
                            id="main-contact-last-name"
                            required
                            v-model="main_contact_last_name"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('position_in_the_company') + ':'"
                      id="main-contact-position-group"
                      label-for="main-contact-position"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Position in the Company"
                        rules="required"
                    >
                      <b-input-group class="input-group-merge">
                        <b-input-group-prepend is-text>
                          <feather-icon
                              class="cursor-pointer"
                              icon="BriefcaseIcon"
                          />
                        </b-input-group-prepend>
                        <b-form-input
                            :disabled="disableInputs"
                            :state="errors.length > 0 ? false : null"
                            id="main-contact-position"
                            required
                            v-model="main_contact_position"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
          </b-form>
        </validation-observer>
      </p>
    </b-modal>
    <b-modal
        hide-header-close
        no-close-on-backdrop
        scrollable
        size="lg"
        v-model="newDebtor"
    >
      <template #modal-footer="{}">
        <b-button @click="cancelNewDebtor()" size="sm" variant="outline-primary">
          {{ $t("back") }}
        </b-button>
        <b-button @click="saveNewDebtor()" size="sm" variant="primary">
          {{ $t("save") }}
        </b-button>
      </template>
      <p class="">
        <validation-observer
            name="RegisterCompany"
            ref="companyRegisterValidation"
        >
          <b-form>
            <h5>{{ $t("main_contact_person") }}</h5>
            <b-card>
              <b-row m-0 p-0>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('salutation') + ':'"
                      id="main-contact-gender-group"
                      label-for="main-contact-gender"
                  >
                    <b-input-group class="input-group-merge">
                      <b-input-group-prepend is-text>
                        <feather-icon class="cursor-pointer" icon="UserIcon"/>
                      </b-input-group-prepend>
                      <b-form-select
                          :options="[
                          {
                            text: $t('select_one'),
                            value: null,
                            disabled: true,
                          },
                          { text: $t('mr'), value: '0' },
                          { text: $t('mrs'), value: '1' },
                        ]"
                          id="main-contact-gender"
                          required
                          v-model="main_contact_gender"
                      ></b-form-select>
                    </b-input-group>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('company_email_address') + ':'"
                      id="main-contact-email-group"
                      label-for="main-contact-email"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Number of Debtors"
                        rules="required|email"
                    >
                      <b-input-group class="input-group-merge">
                        <b-input-group-prepend is-text>
                          <feather-icon
                              class="cursor-pointer"
                              icon="MailIcon"
                          />
                        </b-input-group-prepend>
                        <b-form-input
                            :state="errors.length > 0 ? false : null"
                            id="main-contact-email"
                            required
                            v-model="main_contact_email"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row m-0 p-0>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('first_name') + ':'"
                      id="main-contact-first-name-group"
                      label-for="main-contact-first-name"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="First name"
                        rules="required"
                    >
                      <b-input-group class="input-group-merge">
                        <b-input-group-prepend is-text>
                          <feather-icon
                              class="cursor-pointer"
                              icon="UserIcon"
                          />
                        </b-input-group-prepend>
                        <b-form-input
                            :disabled="disableInputs"
                            :state="errors.length > 0 ? false : null"
                            id="main-contact-first-name"
                            required
                            v-model="main_contact_first_name"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('tel') + ':'"
                      id="main-contact-tel-group"
                      label-for="main-contact-tel"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="tel"
                        rules="required"
                    >
                      <b-input-group>
                        <b-input-group-prepend id="phoneCountries">
                          <b-form-select
                              :options="mobileCountries"
                              v-model="phoneContries"
                          >
                          </b-form-select>
                        </b-input-group-prepend>
                        <b-form-input
                            :state="errors.length > 0 ? false : null"
                            @input="checkPhoneFormat"
                            class="form-control"
                            id="main-contact-tel"
                            v-model="main_contact_tel"
                        >
                        </b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                      <small class="text-danger" v-if="phoneValidation">{{
                          phoneValidation
                        }}
                      </small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row m-0 p-0>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('last_name') + ':'"
                      id="main-contact-last-name-group"
                      label-for="main-contact-last-name"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Last Name"
                        rules="required"
                    >
                      <b-input-group class="input-group-merge">
                        <b-input-group-prepend is-text>
                          <feather-icon
                              class="cursor-pointer"
                              icon="UserIcon"
                          />
                        </b-input-group-prepend>
                        <b-form-input
                            :disabled="disableInputs"
                            :state="errors.length > 0 ? false : null"
                            id="main-contact-last-name"
                            required
                            v-model="main_contact_last_name"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="6" md="6" xl="6">
                  <b-form-group
                      :label="$t('position_in_the_company') + ':'"
                      id="main-contact-position-group"
                      label-for="main-contact-position"
                  >
                    <validation-provider
                        #default="{ errors }"
                        name="Position in the Company"
                        rules="required"
                    >
                      <b-input-group class="input-group-merge">
                        <b-input-group-prepend is-text>
                          <feather-icon
                              class="cursor-pointer"
                              icon="BriefcaseIcon"
                          />
                        </b-input-group-prepend>
                        <b-form-input
                            :disabled="disableInputs"
                            :state="errors.length > 0 ? false : null"
                            id="main-contact-position"
                            required
                            v-model="main_contact_position"
                        ></b-form-input>
                      </b-input-group>

                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>
          </b-form>
        </validation-observer>
      </p>
    </b-modal>
    <!-- change discount in % modal -->

    <b-modal :title="$t('discount_in_%')" hide-footer v-model="modalStatus" @show="changeDiscountValue">
      <b-input-group class="input-group-merge">
        <b-form-input
            id="discount_in_percentage"
            required
            v-model="Discountdata.discount_in_percentage"
            @input="changeDiscountValue"
        ></b-form-input>
      </b-input-group>

      <p
          class="p-2 mt-1 b-radius-lg bg-light text-dark"
          v-if="Discountdata.new_effective_value"
      >
        {{ $t('effective_value') }}:
        <b>{{ amountFormat(CalculateNewEffectiveValue(Discountdata.discount_in_percentage, item), item.currency.name) }}</b>
      </p>

      <p
          class="p-2 mt-1 b-radius-lg bg-light text-dark"
          v-if="Discountdata.new_discount"
      >
        {{ $t('discount') }}:
        <b>{{ amountFormat(Discountdata.new_discount, item.currency.name) }}</b>
      </p>

      <b-form-checkbox
          class="mt-1"
          switch
          v-model="Discountdata.save_new_discount_in_percentage"
          variant="success"
      >
        {{ $t('save_percentage_future_invoices') }}
      </b-form-checkbox>

      <template>
        <b-button
            class="btn mt-1"
            variant="primary"
            @click="changeDiscount"
        >
          {{ $t('change') }}
        </b-button>
      </template>
    </b-modal>

    <ChangeRateModal
        v-if="refundModal"
        :isVisible.sync="refundModal"
        :invoice="invoice"
        :amountFormat="amountFormat"
        :calculateRefundIfDefaults="calculateRefundIfDefaults"
        :type="type"
    />
  </b-card>
</template>


<script>
import {ValidationProvider, ValidationObserver} from "vee-validate";
import fileDownload from "js-file-download";
import ApproveModal from "@/components/Common/ApproveModal";
import AuditTrial from "@/components/Invoice/AuditTrial.vue";
import StatisticCardHorizontal from "@core/components/statistics-cards/StatisticCardHorizontal";
import InvoiceStatus from "@/components/Invoice/InvoiceStatus";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import i18n from "@/libs/i18n";
import AddNewCompnayModal from "./AddNewCompanyModal.vue";
import MainContactModal from "./MainContactModal.vue";
import {phone} from "phone";

import {VueAutosuggest} from "vue-autosuggest";
import Cleave from "vue-cleave-component";
import {CurrencyAmountFormat} from '@core/utils/CurrencyAmountFormat';
import ChangeRateModal from "@/components/Common/ChangeRateModal.vue";
import SetAutoRemoveDaysAdmin from "@/components/Common/SetAutoRemoveDaysAdmin.vue";
import CalculationsHelper from "@/helpers/calculationsHelper";


export default {

  data() {
    return {
      factoringType: null,
      currencyId: null,
      currencies: null,
      currencyName: null,
      absoluteFee: 0,
      modalStatus: false,
      refundModal: false,
      defaultRefundModal: false,
      type: '',
      reason: '',
      selectNewDebtor: false,
      company_uid: "",
      main_contact_gender: "",
      main_contact_email: "",
      main_contact_first_name: "",
      main_contact_last_name: "",
      main_contact_position: "",

      Discountdata: {
        discount_in_percentage: "",
        new_discount: "",
        new_effective_value: "",
        save_new_discount_in_percentage: false,
      },

      main_contact_tel: "",
      phoneContries: "CH",
      phoneCountriesList: [],
      countries: [],
      mobileCountries: [],
      phoneValidation: "",
      newDebtor: false,
      accept_recourse: true,
      recourse: "",
      showUpdateButton: true,
      addNewComp: false,
      companyZefixData: null,
      invoiceModal: null,
      load: true,
      uploadFileModal: false,
      associated: null,
      MainContact: false,
      companyZefixDataMainContact: null,
      uploadedInvoce: {
        file: null,
        id: "",
      },
      fileTypeOptions: [
        {
          text: i18n.t("proof_of_ownership"),
          value: "Proof Of Ownership",
        },
        {
          text: i18n.t("notice_of_assignment"),
          value: "Notice Of Assignment",
        },
        {
          text: i18n.t("other"),
          value: "Other",
        },
      ],
      fileType: "",
      fileDesc: "",
      items: [],
      showHint: false,
      companyMessage: "",
      amountOptions: {
        customDelimiter: {
          delimiters: ["'", "."],
          blocks: [2, 3, 2],
          uppercase: true,
        },
      },
      amount: "",
      fields: [
        {
          label: this.$t("score"),
          key: "score",
          variant: "danger",
          thClass: "bg-light-danger text-dark",
        },
        {
          label: this.$t("days_to_due_refactor"),
          key: "terms",
          variant: "danger",
          thClass: "bg-light-danger text-dark",
        },
        {
          label: this.$t("date_of_scoring"),
          key: "created_at",
          variant: "danger",
          thClass: "bg-light-danger text-dark",
        },
        {
          label: this.$t("seller"),
          key: "seller",
          variant: "danger",
          thClass: "bg-light-danger text-dark",
        },
        {
          label: this.$t("debtor"),
          key: "debtor",
          variant: "danger",
          thClass: "bg-light-danger text-dark",
        },
      ],
      fractionFields: [
        {
          label: "date",
          align: "start",
          key: "created_at",
          variant: "success",
          thClass: "bg-light-success text-dark",
        },
        {
          label: "invoice_id",
          align: "start",
          key: "invoice_id",
          variant: "success",
          thClass: "bg-light-success text-dark",
        },

        {
          label: "buyer",
          align: "start",
          key: "buyer",
          variant: "success",
          sortable: false,
          thClass: "bg-light-success text-dark",
        },

        {
          label: "currency",
          align: "start",
          variant: "success",
          key: "currency",
          sortable: false,
          thClass: "bg-light-success text-dark",
        },
        {
          label: "amount",
          align: "start",
          variant: "success",
          key: "amount",
          sortable: false,
          thClass: "bg-light-success text-dark",
        },
        {
          label: "fraction",
          align: "start",
          key: "fraction",
          variant: "success",
          sortable: false,
          thClass: "bg-light-success text-dark",
        },

        {
          label: "discount",
          align: "start",
          variant: "success",
          key: "discount",
          sortable: false,
          thClass: "bg-light-success text-dark",
        },
        {
          label: "hive_fee",
          align: "start",
          variant: "success",
          key: "hive_fee",
          sortable: false,
          thClass: "bg-light-success text-dark",
        },
        {
          label: "net_return",
          align: "start",
          key: "net_return",
          sortable: false,
          variant: "success",
          thClass: "bg-light-success text-dark",
        },
        {
          label: "status",
          key: "status",
          customSlot: true,
          sortable: false,
          variant: "success",
          thClass: "bg-light-success text-dark",
        },
        {
          label: "",
          key: "custom",
          align: "end",
          customSlot: true,
          sortable: false,
          variant: "success",
          thClass: "bg-light-success text-right",
          tdClass: "text-right",
        },
      ],
      filteredOptions: [],
      options: [
        {
          value: 0,
          name: i18n.t("Non-Recourse"),
          title: 'non_recourse_tooltip_title'
        },
        {
          value: 1,
          name: i18n.t("Recourse"),
          title: 'recourse_tooltip_title'
        },
        {
          value: 2,
          name: i18n.t("Reverse"),
          title: 'reverse_tooltip_title'
        },
      ],
      companies: [],
      approveModalAction: null,
      modalText: "",
      modalTitle: "",
      auditTrial: false,
      showAutoSuggest: false,
      disabledSumbit: true,
      errorDueDate: "",
      numbersAmountError: "",
      fraction: null,
      hasOwnership: false,
      scoringModal: false,
      defaultModal: false,
      defaulting: {
        refund_if_defaults_percent: 0,
        refund_if_defaults_amount: 0,
        voluntary_paid_out_percent: 0,
        voluntary_paid_out: 0
      },
      refund_1_percent: 0,
      custom_scoring: {
        seller_score: "",
        debtor_score: "",
        terms: "",
        due_days: "",
        amount: ""
      },
      item: {},
      disableInputs: false,
      selected_company: null,
      CalculationsHelper,
    };
  },
  components: {
    SetAutoRemoveDaysAdmin,
    ChangeRateModal,
    InvoiceStatus,
    StatisticCardHorizontal,
    ValidationProvider,
    ValidationObserver,
    ApproveModal,
    AddNewCompnayModal,
    AuditTrial,
    VueAutosuggest,
    Cleave,
    MainContactModal,
  },
  props: ["invoice"],
  computed: {

    buttonConfigs() {
      const { status, hide_from_market } = this.item;
      const isSuperAdmin = this.adminType(['super_admin']);

      return [
        {
          action: 'activate',
          title: this.$t('set_active'),
          variant: 'outline-success',
          icons: ['CheckIcon'],
          modal: 'approveModal',
          message: this.$t('are_you_sure_you_want_set_to_active'),
          visible: status !== 1 && status !== 10
        },
        {
          action: 'send_confirmation',
          title: this.$t(this.factoringType === 'reverse' ? 'send_supplier_confirmation' : 'send_initial_debtor_confimration'),
          variant: 'outline-dark',
          icons: ['MailIcon'],
          modal: 'approveModal',
          visible: status === 5 || status === 6
        },
        {
          action: 'send_offer',
          title: this.$t('send_offer'),
          variant: 'outline-warning',
          icons: ['SendIcon'],
          modal: 'approveModal',
          visible: status !== 1 && status !== 10 && status !== 11
        },
        {
          action: 'republish',
          title: this.$t('republish'),
          variant: 'outline-primary',
          icons: ['RefreshCcwIcon'],
          modal: 'approveModal',
          visible: status === 15
        },
        {
          action: 'settled',
          title: this.$t('settled'),
          variant: 'outline-warning',
          icons: ['CheckCircleIcon'],
          modal: 'approveModal',
          visible: status === 10
        },
        {
          action: 'default',
          title: this.$t('set_to_default'),
          variant: 'outline-danger',
          icons: ['ZapOffIcon'],
          visible: status === 10 && isSuperAdmin,
          customHandler: true
        },
        {
          action: 'hide_from_market',
          title: this.$t(hide_from_market === 1 ? 'show_in_market' : 'hide_from_market'),
          variant: hide_from_market === 1 ? 'outline-success' : 'outline-danger',
          icons: [hide_from_market === 1 ? 'EyeIcon' : 'EyeOffIcon'],
          modal: 'approveModal',
          message: this.$t(hide_from_market === 1
              ? 'are_you_sure_you_want_to_show_this_invoice'
              : 'are_you_sure_you_want_to_hide_this_invoice'),
          visible: hide_from_market == 1 || ( hide_from_market == 0  && isSuperAdmin )
        },
        {
          action: 'reject',
          title: this.$t('reject_the_invoice'),
          variant: 'outline-danger',
          icons: ['XIcon'],
          modal: 'approveModal',
          message: this.$t('are_you_sure_you_want_to_reject_the_invoice'),
          visible: isSuperAdmin
        },
        {
          action: 'deactivate',
          title: this.$t('deactivate'),
          variant: 'outline-danger',
          icons: ['SlashIcon'],
          modal: 'approveModal',
          visible: status != 14 && isSuperAdmin
        },
        {
          action: 'remove',
          title: this.$t('remove'),
          variant: 'outline-danger',
          icons: ['TrashIcon'],
          modal: 'approveModal',
          message: this.$t('are_you_sure_you_want_to_remove_this_invoice'),
          visible: status !== 1 && isSuperAdmin
        },

        // {
        //   action: 'force_activate',
        //   title: this.$t('force_activate'),
        //   variant: 'outline-primary',
        //   icons: ['ActivityIcon', 'CheckIcon'],
        //   visible: status === 5 || status === 6,
        //   customHandler: true
        // },
      ];
    },

    visibleButtons() {
      return this.buttonConfigs.filter(btn => btn.visible);
    },

    disabledDates() {
      var date = new Date();
      var today = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return today;
    },
    calculatedVoluntaryAmount() {
      const percentage = parseFloat(this.defaulting.voluntary_paid_out_percent) || 0;
      const refundAmount = parseFloat(this.defaulting.refund_if_defaults_amount) || 0;
      const calculatedAmount = (percentage / 100) * refundAmount;

      this.defaulting.voluntary_paid_out = calculatedAmount.toFixed(2);
      return calculatedAmount.toFixed(2);
    }
  },
  created() {
    this.currencyName = this.invoice.currency.name;
    const amount = Number(this.invoice.amount);
    let fee;
    if(this.invoice && this.invoice.scoring && this.invoice.scoring.investor_fee) {
      fee = Number(this.invoice.scoring.investor_fee);
    }
    this.absoluteFee = amount * (fee / 100);
    this.$http.post("/countries").then((res) => {
      res.data.value.map((item) => {
        this.mobileCountries.push({
          value: item.iso_code,
          text: `${item.iso_code} (${item.phone_code})`,
        });
      });
    });
    this.getCompanies();
    this.item = this.invoice;
    this.Discountdata.discount_in_percentage = Number(this.item.scoring?.discount_in_percentage).toFixed(2);
    if (this.item) {
      this.custom_scoring.amount = this.invoice.amount.replaceAll("'", "");
      this.custom_scoring.seller_score = this.item.seller_score;
      this.custom_scoring.debtor_score = this.item.debtor_score;
      this.custom_scoring.terms = this.item.terms;
      // Calculate due_days dynamically from today to the due date
      if (!this.calculateDueDays()) {
        this.custom_scoring.due_days = this.item.due_days || 0;
      }
      this.amount = this.item.amount;
      this.load = false;
      if (this.item.associate_files.length > 0) {
        const hasProofType = this.item.associate_files.some(item => item.hasOwnProperty('type') && item.type === 'Proof Of Ownership');
        if (hasProofType) {
          this.hasOwnership = true;
        } else {
          this.hasOwnership = false;
        }
      }

      this.factoringType = CalculationsHelper.getType(this.item)

      if (this.item.is_reverse == 1) {
        this.item.recourse = 2;
      } else if (this.item.recourse == 0) {
        this.item.recourse = 0;
      } else {
        this.item.recourse = 1;
      }
      if (this.item.issuer_id) {
        this.showAutoSuggest = true;
      }

      if (this.invoice && this.invoice.debtor) {
        this.invoice.company_name = this.item.debtor.name;
      }
      this.parsedAmount(this.item.amount);
      this.getCustomScoring();
      this.getCurrencies();
    }

    this.changeTerms();
  },

  methods: {

    calculateDueDays() {
      // Calculate due_days dynamically from today to the due date
      if (this.item.due_date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate calculation
        const dueDate = new Date(this.item.due_date);
        dueDate.setHours(0, 0, 0, 0);
        const differenceInTime = dueDate.getTime() - today.getTime();
        const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));
        this.custom_scoring.due_days = differenceInDays >= 0 ? differenceInDays : 0;
        return this.custom_scoring.due_days;
      }
      return null;
    },

    handleButtonClick(button) {
      if (button.customHandler) {
        this.handleCustomAction(button.action);
      } else {
        this.changeModalInfo(
            button.action,
            button.title,
            button.message || this.$t('are_you_sure')
        );
      }
    },

    handleCustomAction(action) {
      if (action === 'default') {
        this.openInvoiceDefault();
      }
    },

    getInvoiceBadgeVariant(item) {
      const successStatuses = [1, 9, 10];
      return (item.hide_from_market == 0 && successStatuses.includes(item.status)) ? 'success' : 'danger';
    },

    getInvoiceBadgeText(item) {
      const successStatuses = [1, 9, 10];
      return (item.hide_from_market == 0 && successStatuses.includes(item.status)) ? 'invoice_visible' : 'invoice_not_visible';
    },

    changeCurrency(currencyId, currencyName) {
      this.currencyId = currencyId;
      this.currencyName = currencyName;
    },

    getCurrencies() {
      this.$http.post("/currency").then((response) => {
        if (response.data.status == 200) {
          let allCurrencies = response.data.value;

          this.currencies = allCurrencies.filter((e)=> e.role === 'all');
        }
      });
    },

    CalculateNewEffectiveValue(newDiscountPercentage, invoice) {
      if(!invoice?.scoring) return null;

      const invoiceClone = structuredClone(invoice);
      invoiceClone.scoring.discount_in_percentage = newDiscountPercentage;

      return this.CalculationsHelper.getLiquidityAmount(invoiceClone);
    },

   calculateRefundIfDefaults(item) {
      if (item.scoring.refund_if_defaults) {
        let refund_amount = CalculationsHelper.getRefundDefaultAmount(item);
        this.defaulting.refund_if_defaults_amount = refund_amount
        return refund_amount;
      } else {
        return 0;
      }
    },

    amountFormat(amount, currency) {
      return CurrencyAmountFormat(amount, currency)
    },

    refundIfDefaultsPercentage(invoice) {
      if (invoice.scoring.refund_if_defaults) {
        const refund = CalculationsHelper.getRefundDefaultPercentage(invoice);
        this.defaulting.refund_if_defaults_percent = refund
        return refund;
      } else {
        return 0;
      }
    },

    sanitizePercent(val) {
      val = String(val).replace(/[^0-9.]/g, "");

      const parts = val.split(".");
      if (parts.length > 2) {
        val = parts[0] + "." + parts.slice(1).join("");
      }

      let num = parseFloat(val);
      if (isNaN(num)) num = "";
      else if (num < 0) num = 0;
      else if (num > 100) num = 100;

      this.defaulting.voluntary_paid_out_percent = num;
      return num;
    },

    changeDiscount() {
      this.$http.post(`/invoice/admin/change_discount/${this.item.id}`, this.Discountdata).then((res) => {
        if (res) {
          if (res) {
            this.showSuccessMessage(res.data.message)
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        }
      });
    },

    changeDiscountValue() {
      let amount = parseFloat(this.item.amount.replace("'", ""));
      this.Discountdata.new_discount = amount * this.Discountdata.discount_in_percentage / 100
      this.Discountdata.new_effective_value = amount - this.Discountdata.new_discount
    },
    cancelSelectedDebtor() {
      this.selectNewDebtor = false;
      this.selected_company = null;
    },
    saveSelectedDebtor() {
      this.selectNewDebtor = false;
      this.newDebtor = true;
    },
    checkPhoneFormat() {
      let validation = phone(this.main_contact_tel, {
        country: this.phoneContries,
        validateMobilePrefix: false
      });

      if (validation && validation.isValid == false) {
        this.phoneValidation = `Phone for ${this.phoneContries} must be valid`;
      } else {
        this.phoneValidation = "";
      }
    },
    cancelNewDebtor() {

    },
    saveNewDebtor() {

      this.$refs.companyRegisterValidation.validate().then((success) => {
        if (success && this.phoneValidation.length < 1) {
          this.disableInputs = true;
          const formSubmissionData = {
            company_id: this.selected_company,
            main_contact_first_name: this.main_contact_first_name,
            main_contact_last_name: this.main_contact_last_name,
            main_contact_position: this.main_contact_position,
            main_contact_tel: this.main_contact_tel,
            main_contact_email: this.main_contact_email,
            main_contact_gender: this.main_contact_gender,
          };

          this.$http.post(`/invoice/admin/addDebtor/${this.$route.params.id}`, formSubmissionData).then((res) => {
            if (res) {
              if (res.data.status == 200) {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: this.$t("success"),
                    icon: "SuccessIcon",
                    variant: "success",
                  },
                });
                this.newDebtor = false;
                window.location.reload();
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: res.data.message
                        ? res.data.message
                        : "Something went wrong, please try again",
                    icon: "ErrorIcon",
                    variant: "danger",
                  },
                });
              }

            }
          });
        }
        this.disableInputs = false;
      });
    },
    getCustomScoring() {

      this.$http
          .post("/invoice/admin/getCustomScoring/" + this.$route.params.id)
          .then((response) => {
            if (response.data.value) {
              this.custom_scoring.seller_score = response.data.value.seller_score;
              this.custom_scoring.debtor_score = response.data.value.debtor_score;
              this.custom_scoring.terms = response.data.value.terms;
              // Calculate due_days dynamically from today to the due date
              if (!this.calculateDueDays()) {
                this.custom_scoring.due_days = response.data.value.due_days;
              }
            }

          });
    },
    cancel() {
      this.scoringModal = false;
    },
    openCustomScoring() {
      this.scoringModal = true;
    },
    saveCustomScoring() {

      this.$http
          .post("/invoice/admin/custom-score/" + this.$route.params.id, this.custom_scoring)
          .then((response) => {
            if (response) {
              window.location.reload();
            }
          });
    },

    cancelDefault() {
      this.defaultModal = false;
    },

    openInvoiceDefault() {
      this.defaultModal = true;
    },

    saveInvoiceDefault() {
      this.changeStatus("default");
    },

    readInvoice(item) {
      this.$http
          .post(
              "/invoice/admin/download/" + item.id,
              {},
              {
                responseType: "blob",
              }
          )
          .then((response) => {
            let title = item.title + '.pdf'
            fileDownload(response.data, title);
          })
          .catch((error) => {
          });
    },
    returnTitle(item) {
      if (item.file_name) {
        return item.file_name;
      }

      return this.$t(item.title);
    },
    closeMainContactModal(data) {
      if (data != null) {
        this.invoice.issuer_id = data[0].company_id;
      } else {
        this.invoice.company_name = "";
      }
      this.$bvModal.hide("MainContact");
    },
    parsedAmount1($event, val) {
      this.numbersAmountError = "";
      let regex = /^[0-9']*(.)?[0-9]*$/;
      if (regex.test(val)) {
        setTimeout(() => {
          if (val == this.item.amount && val) {
            if (this.item.amount.includes("'")) {
              this.item.amount = this.item.amount.replace(/'/gi, "");
            }
            this.item.amount = Number(this.item.amount)
                .toFixed(2)
                .replace(/\d(?=(\d{3})+\.)/g, "$&'");
          }
        }, 1000);
      } else {
        this.numbersAmountError = this.$t("only_numbers_allowed");
        $event.preventDefault();
        return;
      }
    },
    parsedAmount(val) {
      setTimeout(() => {
        if (val == this.item.amount && val) {
          if (this.item.amount.includes("'")) {
            this.item.amount = this.item.amount.replace(/'/gi, "");
          }
          this.item.amount = Number(this.item.amount)
              .toFixed(2)
              .replace(/\d(?=(\d{3})+\.)/g, "$&'");
        }
      }, 1000);
    },
    closeAddCompModal(data) {
      //this.getCompanies();
      if (data != null) {
        this.invoice.company_name = data[0].name;
        this.invoice.uid = data[0].uid;
        this.invoice.issuer_id = data[0].id;
      } else {
        this.invoice.company_name = "";
      }
      this.$bvModal.hide("AddCompany");
    },
    showSettings() {
      this.addNewComp = false;
      this.openModal = false;
    },
    onSelected(val, item) {
      // value.company_name =
      item.company_name = val.item;

      this.getCompanyDataFromZefix(item);
      // this.successMessage = false;
    },
    getCompanyDataFromZefix(item) {
      const self = this;
      if (typeof this.companies[item.company_name] != "undefined") {
        this.$http
            .post("/get-company-data-from-zefix", {
              search: this.companies[item.company_name],
            })
            .then((res) => {
              if (res) {
                item.company_uid = res.data.value.uid;
                let data = {
                  search: item.company_uid,
                  invoice_id: item.id,
                };

                this.$http.post("/invoice/getCompany", data).then((res) => {
                  if (res) {
                    if (res.data.value && res.data.value.from == "Database") {
                      item.issuer_id = res.data.value.company.id;
                    } else if (
                        res.data.value &&
                        res.data.value.from == "ContactPerson"
                    ) {
                      this.showMainContactModal(item, res.data.value.company);
                    } else {
                      this.showModal(item, res.data.value.company);
                    }
                    this.showUpdateButton = true;
                  }
                });
              }
            });
      }
    },
    showModal(item, company) {
      this.companyZefixData = company;
      this.$bvModal.show("AddCompany");
    },

    showMainContactModal(item, company) {
      this.companyZefixDataMainContact = company;
      this.$bvModal.show("MainContact");
    },

    searchForCompaniesName(item) {
      this.companyMessage = "";
      clearTimeout(this.timeout);
      clearTimeout(this.timeout);
      const self = this;

      this.timeout = setTimeout(function () {
        if (item.company_name.length > 2) {
          self.showHint = true;
          self.companies = [];
          self.filteredOptions = [];
          self.$http
              .post("/get-companies-name-from-zefix", {
                search: item.company_name,
              })
              .then((res) => {
                self.successMessage = false;
                self.filteredOptions.push({
                  data: Object.keys(res.data.value),
                });
                self.showHint = false;
                self.companies = res.data.value;
                if (res.data.value.length < 1) {
                  self.companyMessage =
                      "no_results_with_the_information_you_entered_please_try_again";
                }
              });
        } else {
          self.showHint = false;
          self.companies = [];
          self.filteredOptions = [];
        }
      }, 1000);
    },

    downloadInvoice(item) {
      this.$http
          .post(
              "/invoice/admin/download/" + item.id,
              {},
              {
                responseType: "blob",
              }
          )
          .then((response) => {
            let title = item.title + '.pdf'
            fileDownload(response.data, title);
          })
          .catch((error) => {
          });
    },

    changeTerms(item) {
      if (this.item.issue_date && this.item.due_date) {
        const startDate = new Date(this.item.issue_date);
        const endDate = new Date(this.item.due_date);
        const differenceInTime = endDate.getTime() - startDate.getTime();
        const differenceInDays = differenceInTime / (1000 * 3600 * 24);
        let term = Math.abs(Math.round(differenceInDays));
        this.item.terms = term;

        // Recalculate due_days dynamically from today to the due date
        this.calculateDueDays();

        this.errorDueDate = "";
        this.disabledSumbit = false;
      }
    },

    changeDueDate(item) {
      if (this.item.issue_date && this.item.terms) {
        let newDate = new Date(this.item.issue_date);

        let term = Number(this.item.terms);
        let parsed = new Date(this.item.issue_date);

        let compareDate = "";
        newDate.setDate(parsed.getDate() + term);
        compareDate = newDate;

        this.item.due_date = newDate.toISOString().split("T")[0];

        let max = 0;
        if (term <= 30) {
          max = 10;
        } else if (term <= 45) {
          max = 12;
        } else if (term <= 60) {
          max = 15;
        } else if (term <= 75) {
          max = 17;
        } else if (term > 75) {
          max = 20;
        }
        compareDate.setDate(compareDate.getDate() - max);
        var today = new Date();
        if (compareDate > today) {
          this.errorDueDate = "";
          this.disabledSumbit = false;
        } else {
          this.errorDueDate = this.$t("due_date_issue_text");
          this.disabledSumbit = true;
        }
      }
    },

    editInvoice() {
      if (this.numbersAmountError.length < 1) {
        let data = {
          debtor_id: this.item.company_id,
          issuer_id: this.item.issuer_id,
          issue_date: this.item.issue_date,
          due_date: this.item.due_date,
          terms: Number(this.item.terms),
          amount: this.item.amount.replace(/'/gi, ""),
          invoice_number: this.item.invoice_number,
          status: this.item.status,
          accept_recourse: this.accept_recourse,
          bic: this.item.bic,
          iban: this.item.iban,
          bank_address: this.item.bank_address,
          bank_name: this.item.bank_name,
          currency_id: this.currencyId
        };

          const appLoading = document.getElementById('loading-bg')
          if (appLoading) {
            appLoading.style.display = 'block'
            appLoading.style.zIndex = '100'
            appLoading.style.position = 'fixed'
            document.body.style.overflow = 'hidden'
          }

          this.$http.post(`/invoice/admin/update-without-scoring/${this.$route.params.id}`, data).then((res) => {
            if (res) {
              this.showSuccessMessage(i18n.t("invoice_updated_successfully"));
              window.location.reload();
            }

          });
      }
    },
    changeInvoiceFact(type) {
      let data = {};

      if (type != 'non_recourse' && this.accept_recourse == false) {
        return;
      }

      this.$http.post(`/invoice/admin/change/${type}/${this.$route.params.id}`, data).then((res) => {
        if (res) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: i18n.t("success"),
              icon: "SuccessIcon",
              variant: "success",
            },
          });
        }
      });

    },
    triggerFileInput() {
      this.$refs.fileInput1.$refs.input.click();
    },
    storeData() {
      let formData = new FormData();
      let config = {headers: {"Content-Type": "multipart/form-data"}};
      formData.append("pdf", this.associatedEvent.target.files[0]);
      this.$http
          .post(
              `/invoice/admin/associate/upload/${this.$route.params.id}`,
              formData,
              config
          )
          .then((res) => {
            if (res) {
              let data = {
                description: this.fileDesc,
                type: this.fileType,
              };
              this.$http
                  .post(`/invoice/admin/associateData/${res.data.value.id}`, data)
                  .then((response) => {
                    if (response) {
                      this.uploadFileModal = false;
                      this.showSuccessMessage(
                          this.$t("file_uploaded_successfully")
                      );
                      this.associated = null;
                      this.fileType = "";
                      this.fileDesc = "";
                      window.location.reload();
                    }
                  });
            }
          });
    },

    uploadFile(event) {
      this.fileType = "";
      this.fileDesc = "";
      this.uploadedInvoce.file = event.target.files[0];
      this.associatedEvent = event;
      this.uploadFileModal = true;
    },
    showDetails() {
      this.auditTrial = false;
    },
    changeModalInfo(action, title, text, item) {
      this.approveModalAction = action;
      this.modalTitle = title;

      this.fraction = item;
      if (action == 'activate') {
        this.allowLevel();
      } else {
        this.modalText = text;
      }
    },
    allowLevel() {
      this.$http.post(`/invoice/admin/check-level/${this.$route.params.id}`).then((response) => {
        if (response.data.value.allowed == true) {
          this.modalText = this.$t('are_you_sure_you_want_set_to_active');
        } else {
          this.modalText = this.$t('your_level_does_not_allow_to_publish_this_invoice') + ''
              + this.$t('are_you_sure_you_want_set_to_active');
        }
      });
    },
    callApproveModalAction(acknowledgmentType = null, recipiants = null) {
      if (this.approveModalAction == "remove") {
        this.changeStatus("remove");
      } else if (this.approveModalAction == "approve") {
        this.changeStatus("approve");
      } else if (this.approveModalAction == "reject") {
        this.changeStatus("reject");
      } else if (this.approveModalAction == "pending") {
        this.changeStatus("pending");
      } else if (this.approveModalAction == "scoreDecline") {
        this.changeStatus("scoreDecline");
      } else if (this.approveModalAction == "scoreApprove") {
        this.changeStatus("scoreApprove");
      } else if (this.approveModalAction == "proofOfOwnership") {
        this.changeStatus("proofOfOwnership");
      } else if (this.approveModalAction == "proofOfOwnershipInProgress") {
        this.changeStatus("proofOfOwnershipInProgress");
      } else if (this.approveModalAction == "settled") {
        this.changeStatus("settled");
      } else if (this.approveModalAction == "activate") {
        this.changeStatus("activate");
      } else if (this.approveModalAction == "deactivate") {
        this.changeStatus("deactivate");
      } else if (this.approveModalAction == "republish") {
        this.changeStatus("republish");
      } else if (this.approveModalAction == "cancel_fraction") {
        this.cancelFraction();
      } else if (this.approveModalAction == "send_confirmation") {
        if (this.factoringType === 'reverse') {
          this.sendSupplierMail();
        } else {
          this.sendInitialDeborMail();
        }
      } else if (this.approveModalAction == "hide_from_market") {
        this.changeMarketVisibility();
      } else if (this.approveModalAction == 'send_offer') {
        this.SendAnOffer(acknowledgmentType ?? null, recipiants ?? null);
      }
    },

    SendAnOffer(acknowledgmentType = null, recipiants = null){
      this.$http
          .post(`/invoice/admin/send_offer/${this.$route.params.id}`,
           { acknowledgmentType: acknowledgmentType, recipientIds: recipiants })
          .then((res) => {
            if (res) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: i18n.t("success"),
                  icon: "SuccessIcon",
                  variant: "success",
                },
              });
              window.location.reload();
            }
          });
    },

    changeMarketVisibility() {
      this.$http
          .post(`/invoice/admin/change/hide_from_market/${this.$route.params.id}`, {
            value: !this.item.hide_from_market
          })
          .then((res) => {
            if (res) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: i18n.t("success"),
                  icon: "SuccessIcon",
                  variant: "success",
                },
              });
              window.location.reload();
            }
          });
    },
    sendSupplierMail() {
      this.$http
          .post(`/invoice/admin/sendSupplierEmail/${this.$route.params.id}`)
          .then((res) => {
            if (res) {
              window.location.reload();
            }
          });
    },
    sendInitialDeborMail() {
      this.$http
          .post(`/invoice/admin/sendDebtorEmail/${this.$route.params.id}`)
          .then((res) => {
            if (res) {
              window.location.reload();
            }
          });
    },
    cancelFraction() {
      this.$http
          .post(`/invoice/admin/fraction/${this.fraction.id}/cancel`)
          .then((res) => {
            if (res) {
              window.location.reload();
            }

          });
    },
    showSuccessMessage(message, type) {
      let text = this.$t(message);
      this.$http
          .post("/invoice/admin/show/" + this.$route.params.id, {})
          .then((response) => {
            this.$swal({
              position: "center",
              icon: "success",
              title: text,
              showConfirmButton: false,
              timer: 1500,
              customClass: {
                confirmButton: "btn btn-primary",
              },
              buttonsStyling: false,
            });
            this.load = false;

            if (response.data.status == 200) {
              this.invoice = response.data.value;
              this.parsedAmount(this.invoice.amount);
              this.invoice.company_name = response.data.value.debtor.name;
            }

            if (type == "remove") {
              this.$router.replace("/invoices");
            }
          })
          .catch((error) => {
            this.load = false;
          });
    },
    downloadFile(item) {
      this.$http.post("/invoice/admin/associate/download/" + item.id,{}, { responseType: "blob" })
      .then((response) => {
        let title;

        if ( item.file_name ) {
          title = item.file_name
        } else {
          title = item.title + '.pdf'
        }

        fileDownload(response.data, title);
      })
    },
    confirmDeleteFile(file) {
      this.$bvModal.msgBoxConfirm(
          this.$t('are_you_sure_you_want_to_delete_this_file', { filename: this.returnTitle(file) }),
          {
            title: this.$t('delete_the_document'),
            size: 'md',
            buttonSize: 'md',
            okVariant: 'danger',
            okTitle: this.$t('delete'),
            cancelTitle: this.$t('cancel'),
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true
          }
      ).then(value => {
        if (value) {
          this.deleteAssociatedFile(file);
        }
      })
      .catch(err => {
        console.error('Error showing confirmation:', err);
      });
    },

    async deleteAssociatedFile(file) {
      try {

        const response = await this.$http.post(`/invoice/admin/associate/delete/${file.id}`);

        if (response) {
          window.location.reload()
        }
      } catch (error) {
        console.error('Error deleting file:', error);
      } finally {
        this.$store.commit('app/UPDATE_LOADING', false);
      }
    },
    changeStatus(type) {
      let status = 0;
      let message = "";
      let route = "";

      if (type == "activate") {
        status = 1;
        message = "status_changed_successfully";
      }

      if (type == "deactivate") {
        status = 14;
        message = "status_changed_successfully";
      }

      if (type == "archive") {
        status = 2;
        message = "invoice_has_been_successfully_archived";
      }

      if (type == "remove") {
        status = 99;
        route = "remove";
        message = "invoice_has_been_successfully_removed";
      }
      if (type == "approve") {
        status = 1;
        message = "the_invoce_has_been_approved";
      }
      if (type == "reject") {
        status = 2
        message = "status_changed_successfully";
      }
      if (type == "scoreApprove") {
        status = 5;
        message = "success";
      }
      if (type == "proofOfOwnershipInProgress") {
        status = 6;
        message = "success";
      }
      if (type == "settled") {
        status = 11;
        message = "success";
      }
      if (type == "scoreDecline") {
        status = 7;
        message = "success";
      }
      if (type == "default") {
        status = 13;
        message = "success";
      }

      if (type == "republish") {
        status = 1;
        message = "success";
      }

      let data = {
        status: status,
        reason: this.reason
      };

      if (type == "default") {
        data.voluntary_paid_out = this.defaulting.voluntary_paid_out;
        data.voluntary_paid_out_percent = this.defaulting.voluntary_paid_out_percent;
      }

      this.$http
          .post(`/invoice/admin/changeStatus/${this.$route.params.id}`, data)
          .then((res) => {
            if (res) {
              if (res.data.message) {
                this.showSuccessMessage(this.$t(res.data.message), route);
              } else if (res.data.value.msg) {
                this.showSuccessMessage(this.$t(res.data.value.msg), route);
              } else {
                this.showSuccessMessage(this.$t(message), route);
              }
              window.location.reload();
            }
          });
    },

    getCompanies() {
      this.$http.post("/company/admin/allList", {}).then((response) => {
        if (response.data.status == 200) {
          this.companies = response.data.value;
        }
      });
    },
  },
};
</script>
<style>
.scorings-table thead th {
  background: red !important;
}
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-autosuggest.scss";

#fraction-table thead tr th {
  border: none !important;
}

#fraction-table tbody tr:last-child td:first-child {
  border-radius: none !important;
}

.w-fit{
  width: fit-content;
}

.w-188 {
  width: 188px;
}

.info-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.info-card {
  padding: 0.75rem;
  margin-bottom: 0.5rem;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
  min-height: 60px;
}

.info-label {
  font-weight: 700;
  font-size: 14px;
  color: #6c757d;
  display: block;
}

.info-value {
  font-weight: 600;
  font-size: 1rem;
  color: #2c3e50;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.badge {
  white-space: normal;
}

@media (max-width: 575px) {
  .info-card {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .info-content {
    padding: 0.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    align-items: center;
  }

  .info-label {
    font-size: 0.8rem;
  }

  .info-value {
    font-size: 0.95rem;
  }
}

@media (min-width: 576px) and (max-width: 767px) {
  .info-card {
    flex: 0 0 50%;
    max-width: 50%;
  }
}

@media (min-width: 768px) and (max-width: 991px) {
  .info-card {
    flex: 0 0 33.333%;
    max-width: 33.333%;
  }
}

@media (min-width: 992px) and (max-width: 1199px) {
  .info-card {
    flex: 0 0 25%;
    max-width: 25%;
  }
}

@media (min-width: 1200px) {
  .info-row .info-card[class*="md-2"] {
    flex: 0 0 16.666667%;
    max-width: 16.666667%;
  }

  .info-row .info-card[class*="md-3"] {
    flex: 0 0 25%;
    max-width: 25%;
  }

  .info-row .info-card[class*="md-4"] {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
  }
}

.d-flex.align-items-center {
  gap: 0.5rem;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
}

@media (max-width: 767px) {
  .alert {
    padding: 1rem !important;
  }

  .info-row {
    margin-bottom: 0.5rem;
  }
  .main-invoice-section {
    padding: 5px;
  }
}

.nowrap-label > span {
  white-space: nowrap;
}

</style>
